import {ReactElement, RefObject, useEffect, useState} from 'react'
import {cn, debounce} from '@/utilities/helpers'
import {useAsync} from '@/hooks/useAsync'
import {useTranslation} from '@/translations/i18n'
import {
    Combobox,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger
} from '@/components/ui/combobox-atoms'
import {MarkerPin01Icon, SearchLgIcon} from '@components/ui/icon'
import {Spinner} from '@components/ui/spinner'
import {InputHelpText} from '@components/ui/input-help-text'
import useAddressGeocoding from '@features/settings/hooks/useAddressGeocoding'
import {Coordinates} from '@features/settings/types'
import {Separator} from '@components/ui/separator'

type PredictionType = 'geocode' | 'establishment' | 'address' | `(regions)` | `(cities)`

interface InputAddressAutocompleteProps {
    trigger?: ReactElement
    onChange: (value: google.maps.GeocoderResult, latLng: Coordinates) => void
    onLatLngFound?: (value: Coordinates | undefined) => void
    placeholder?: string
    predictionTypes: PredictionType[]
    disabled?: boolean
    staticView?: boolean
    className?: string
    anchor?: RefObject<HTMLElement | null>
}

export const InputAddressAutocomplete = ({
    trigger,
    onChange,
    onLatLngFound,
    placeholder,
    predictionTypes,
    disabled = false,
    staticView = false,
    className,
    anchor
}: InputAddressAutocompleteProps) => {
    const {t} = useTranslation()
    const [open, setOpen] = useState(false)
    const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[] | null>(null)
    const [hasSearched, setHasSearched] = useState(false)
    const searchPlace = useAsync()

    const searchPlaces = debounce(async (value: string) => {
        if (!value) {
            setPredictions(null)
            setHasSearched(false)
            return
        }

        try {
            const autocomplete = new google.maps.places.AutocompleteService()
            const result = await searchPlace.run(
                autocomplete.getPlacePredictions({
                    input: value,
                    types: predictionTypes
                })
            )
            setPredictions(result?.predictions ?? null)
            setHasSearched(true)
        } catch (err) {
            console.error(err)
        }
    }, 750)

    //Geocoder's logic and utils
    const {retrieveLatLng, retrieveStructured, resetGeocoding, addressFormat, addressLatLng} = useAddressGeocoding({
        onLatLngFound
    })

    // On select value retrieve all the geocoding data
    const handleSelectAddress = async (placeId: string) => {
        if (!predictions || !placeId) return
        const address = predictions.find(prediction => prediction.place_id == placeId)?.description
        if (address) {
            await retrieveLatLng(address)
            await retrieveStructured(placeId)
            setOpen(false)
        }
    }

    useEffect(() => {
        if (addressFormat && addressLatLng) {
            onChange(addressFormat, addressLatLng)
            resetGeocoding()
        }
    }, [addressFormat, addressLatLng])

    const content = (
        <>
            <div className="flex flex-col w-full gap-2">
                <ComboboxInput
                    startSlot={<SearchLgIcon size={20} className="text-neutral-500" />}
                    placeholder={placeholder}
                    variant={staticView ? 'primary' : 'ghost'}
                    inputSize="lg"
                    className="py-4 md:p-0"
                />

                {staticView && <InputHelpText helpText={t('commons:select_address_reminder')} />}
            </div>
            {!staticView && (
                <div className="flex flex-col w-full">
                    <Separator orientation="horizontal" />

                    <div className="py-2 px-4">
                        <InputHelpText helpText={t('commons:select_address_reminder')} />
                    </div>
                </div>
            )}

            <ComboboxList>
                {searchPlace.isLoading ? (
                    <div className="flex w-full justify-center p-4">
                        <Spinner size={16} />
                    </div>
                ) : searchPlace.isError ? (
                    <div className="flex w-full justify-center p-4">
                        <InputHelpText error={t('errors:default')} />
                    </div>
                ) : predictions && predictions.length > 0 ? (
                    predictions.map(prediction => (
                        <ComboboxItem
                            key={prediction.place_id}
                            value={prediction.place_id}
                            className="py-2.5 px-2 md:p-4"
                        >
                            <MarkerPin01Icon size={20} className="text-neutral-500" />
                            {prediction.description}
                        </ComboboxItem>
                    ))
                ) : (
                    hasSearched && (
                        <div className="flex w-full flex-col items-center gap-1 p-4 text-center text-md/6">
                            <h3 className="font-medium text-neutral-900">{t('commons:address_not_found')}</h3>
                            <p className="max-w-75 text-neutral-700">{t('commons:address_not_found_description')}</p>
                        </div>
                    )
                )}
            </ComboboxList>
        </>
    )

    return (
        <Combobox
            items={predictions?.map(prediction => prediction.place_id) ?? []}
            filter={null}
            open={staticView ? true : open}
            onOpenChange={setOpen}
            onInputValueChange={searchPlaces}
            onValueChange={(value: string | null) => value && handleSelectAddress(value)}
            disabled={disabled}
        >
            {staticView ? (
                <div className={cn('px-4 md:p-0', className)}>{content}</div>
            ) : (
                <>
                    {!!trigger && <ComboboxTrigger disabled={disabled} render={trigger} />}
                    <ComboboxContent
                        align="end"
                        anchor={anchor}
                        positionerClassName="max-w-(--available-width)"
                        className={cn('px-4 md:p-0', className)}
                    >
                        {content}
                    </ComboboxContent>
                </>
            )}
        </Combobox>
    )
}
